import { isEmpty } from './empty';

type TWithoutUndefined<TPType> = TPType extends undefined ? never : TPType;
type TJustUndefined<TPType> = TPType extends undefined ? undefined : never;

// ---------------------------------------------------------------------------------------------------------------------

type TNullable<TPValue> = TPValue | null;

function isNull<TPValue>(result: TNullable<TPValue>): result is null {
  return result === null;
}

function isNotNull<TPValue>(result: TNullable<TPValue>): result is TPValue {
  return result !== null;
}

// ---------------------------------------------------------------------------------------------------------------------

type TOptional<TPValue> = TPValue | undefined;
type TMaybe<TPValue> = TPValue | null | undefined | void;

function isDefined<TPValue>(value: TMaybe<TPValue>): value is TPValue {
  return !isNil(value);
}

function isUndefined<TPValue>(value: TMaybe<TPValue>): value is undefined {
  return value === undefined;
}

function isNil<TPValue>(value: TMaybe<TPValue>): value is null | undefined | void {
  return value === null || value === undefined || value === void 0;
}

// ---------------------------------------------------------------------------------------------------------------------

function mapIfDefined<TPValue, TPResult>(
  value: TPValue | undefined | null,
  map: (value: TPValue) => TPResult
): TPResult | undefined;
function mapIfDefined<TPValue, TPResult>(
  value: TMaybe<TPValue>,
  map: (value: TPValue) => Promise<TPResult>
): Promise<TPResult | undefined>;
function mapIfDefined<TPValue, TPResult>(
  value: TPValue | undefined | null,
  map: (value: TPValue) => TPResult
): TPResult | undefined {
  return isNil(value) ? undefined : map(value);
}

function mapIfUndefined<TPValue>(value: TPValue | undefined, map: () => TPValue): TPValue {
  return isNil(value) ? map() : value;
}

function mapIfEmpty<TPValue>(value: TPValue | undefined, map: () => TPValue): TPValue {
  return isEmpty(value) ? map() : (value as TPValue);
}

export {
  TWithoutUndefined,
  TJustUndefined,
  TNullable,
  isNull,
  isNotNull,
  TOptional,
  TMaybe,
  isDefined,
  isUndefined,
  isNil,
  mapIfDefined,
  mapIfUndefined,
  mapIfEmpty,
};
